/**
 * CR500 Phase 2F: Spec Coverage Audit
 * 
 * Provides detailed auditing of which blocks were requested, rendered, skipped,
 * and why. Used for admin-only debugging and parity verification.
 */

import type { DealCardBlockSpec, ResolvedDealCardSpecV2, DealCardSurfaceKey } from "./types";
import { isBlockExtracted, getPlaceholderBlockTypes } from "@/lib/deal-cards/blocks/registry";

// ================================================================
// TYPES
// ================================================================

export type BlockSkipReason = 
  | "disabled_in_spec"
  | "missing_required_data"
  | "visibility_false"
  | "unknown_block_type"
  | "placeholder_not_debug"
  | "feature_gated"
  | "dedupe_claimed";

export interface SkippedBlockEntry {
  blockId: string;
  blockType: string;
  reason: BlockSkipReason;
  details?: string;
}

export interface UnknownBlockEntry {
  blockId: string;
  blockType: string;
  reason: string;
}

export interface SpecAuditResult {
  /** Surface being audited */
  surface: DealCardSurfaceKey;
  /** All blocks requested by the spec (enabled or not) */
  allBlocks: string[];
  /** Blocks enabled in spec (ordered) */
  requestedBlocks: string[];
  /** Blocks that were actually rendered */
  renderedBlocks: string[];
  /** Blocks that were skipped with reasons */
  skipped: SkippedBlockEntry[];
  /** Blocks with unknown/unregistered types */
  unknown: UnknownBlockEntry[];
  /** Placeholder blocks in spec (Phase 3 deferred) */
  placeholderBlocks: string[];
  /** Summary stats */
  stats: {
    requested: number;
    rendered: number;
    skipped: number;
    unknown: number;
    placeholders: number;
  };
}

// ================================================================
// AUDIT CONTEXT (for tracking during render)
// ================================================================

export interface AuditContext {
  /** Blocks that have been rendered */
  renderedBlocks: Set<string>;
  /** Blocks that were skipped with reasons */
  skippedBlocks: Map<string, SkippedBlockEntry>;
  /** Debug mode enabled */
  debugMode: boolean;
}

/**
 * Create a new audit context for a render pass.
 */
export function createAuditContext(debugMode: boolean = false): AuditContext {
  return {
    renderedBlocks: new Set(),
    skippedBlocks: new Map(),
    debugMode,
  };
}

/**
 * Mark a block as rendered in the audit context.
 */
export function markBlockRendered(context: AuditContext, blockId: string): void {
  context.renderedBlocks.add(blockId);
}

/**
 * Mark a block as skipped in the audit context.
 */
export function markBlockSkipped(
  context: AuditContext,
  blockId: string,
  blockType: string,
  reason: BlockSkipReason,
  details?: string
): void {
  context.skippedBlocks.set(blockId, { blockId, blockType, reason, details });
}

// ================================================================
// AUDIT GENERATION
// ================================================================

/**
 * Generate a spec coverage audit for a surface.
 * Call this after rendering to get the full audit report.
 */
export function generateSpecAudit(
  spec: ResolvedDealCardSpecV2,
  surface: DealCardSurfaceKey,
  auditContext: AuditContext
): SpecAuditResult {
  const surfaceSpec = spec.surfaces[surface];
  const placeholderTypes = getPlaceholderBlockTypes();
  
  const allBlocks: string[] = [];
  const requestedBlocks: string[] = [];
  const unknown: UnknownBlockEntry[] = [];
  const placeholderBlocks: string[] = [];
  
  for (const block of surfaceSpec.blocks) {
    allBlocks.push(block.id);
    
    // Check if block type is extracted or still a placeholder
    const isExtracted = isBlockExtracted(block.type);
    const isPlaceholder = placeholderTypes.includes(block.type);
    
    if (isPlaceholder) {
      placeholderBlocks.push(block.id);
    }
    
    if (block.enabled) {
      requestedBlocks.push(block.id);
      
      // Check if block type is unknown (not in registry at all)
      if (!isExtracted && !isPlaceholder) {
        unknown.push({
          blockId: block.id,
          blockType: block.type,
          reason: `Block type "${block.type}" not found in registry`,
        });
      }
    }
  }
  
  // Collect skipped blocks (those enabled in spec but not rendered)
  const skipped: SkippedBlockEntry[] = [];
  
  for (const blockId of requestedBlocks) {
    if (!auditContext.renderedBlocks.has(blockId)) {
      // Check if we have an explicit skip reason
      const skipEntry = auditContext.skippedBlocks.get(blockId);
      if (skipEntry) {
        skipped.push(skipEntry);
      } else {
        // Infer reason
        const block = surfaceSpec.blocks.find(b => b.id === blockId);
        if (block && placeholderTypes.includes(block.type) && !auditContext.debugMode) {
          skipped.push({
            blockId,
            blockType: block?.type || "unknown",
            reason: "placeholder_not_debug",
            details: "Placeholder blocks only render with ?dealCardV2debug=1",
          });
        } else {
          skipped.push({
            blockId,
            blockType: block?.type || "unknown",
            reason: "missing_required_data",
            details: "Block did not render (likely missing required deal data)",
          });
        }
      }
    }
  }
  
  // Add disabled blocks to skipped list
  for (const block of surfaceSpec.blocks) {
    if (!block.enabled) {
      skipped.push({
        blockId: block.id,
        blockType: block.type,
        reason: "disabled_in_spec",
        details: "Block has enabled=false in spec",
      });
    }
  }
  
  const renderedBlocks = Array.from(auditContext.renderedBlocks);
  
  return {
    surface,
    allBlocks,
    requestedBlocks,
    renderedBlocks,
    skipped,
    unknown,
    placeholderBlocks,
    stats: {
      requested: requestedBlocks.length,
      rendered: renderedBlocks.length,
      skipped: skipped.length,
      unknown: unknown.length,
      placeholders: placeholderBlocks.length,
    },
  };
}

/**
 * Format audit result as compact string for console logging.
 */
export function formatAuditCompact(audit: SpecAuditResult): string {
  const { stats, surface } = audit;
  return `[${surface}] req=${stats.requested} rend=${stats.rendered} skip=${stats.skipped} unk=${stats.unknown} ph=${stats.placeholders}`;
}

/**
 * Pre-audit a spec to identify potential issues before rendering.
 * Useful for static analysis in debug/compare modes.
 */
export function preAuditSpec(
  spec: ResolvedDealCardSpecV2,
  surface: DealCardSurfaceKey,
  debugMode: boolean = false
): {
  enabledBlocks: DealCardBlockSpec[];
  placeholderBlocks: DealCardBlockSpec[];
  unknownBlocks: DealCardBlockSpec[];
  warnings: string[];
} {
  const surfaceSpec = spec.surfaces[surface];
  const placeholderTypes = getPlaceholderBlockTypes();
  
  const enabledBlocks: DealCardBlockSpec[] = [];
  const placeholderBlocks: DealCardBlockSpec[] = [];
  const unknownBlocks: DealCardBlockSpec[] = [];
  const warnings: string[] = [];
  
  for (const block of surfaceSpec.blocks) {
    if (!block.enabled) continue;
    
    const isExtracted = isBlockExtracted(block.type);
    const isPlaceholder = placeholderTypes.includes(block.type);
    
    if (isPlaceholder) {
      placeholderBlocks.push(block);
      if (!debugMode) {
        warnings.push(`Block "${block.id}" (${block.type}) is a placeholder and will not render`);
      }
    } else if (!isExtracted) {
      unknownBlocks.push(block);
      warnings.push(`Block "${block.id}" has unknown type "${block.type}"`);
    } else {
      enabledBlocks.push(block);
    }
  }
  
  return { enabledBlocks, placeholderBlocks, unknownBlocks, warnings };
}
